import { slug } from 'cuid'
import decode from 'jwt-decode'
import queryString from 'query-string'
import { Request } from 'express'
import { SectionMetaInfoDTO } from '@west-australian-newspapers/publication-types'
import { BaseClientConfig } from '../client/BaseClientConfig'
import {
    storeCookie,
    retrieveCookie,
    removeCookie,
} from '../data/cookies/cookies'
import { isServerEnvironment } from '../environment'
import { LoginEvent, LoginRefreshEvent } from '../events/user-event-types'
import {
    LoginDetails,
    authCheckAccessToken,
    SubscriptionType,
} from '../data/authentication/reducer'
import { getBaseUrl } from '../routing/url'
import { click_origin_subscribe } from '../ssw'
import { Dispatch } from 'redux'
import { Logger } from 'typescript-log'

export const authCallbackPath = '/connect/callback'
export const authRefreshPath = '/connect/refresh'
export const authLoginPath = '/connect/login'
export const authLogoutPath = '/connect/logout'
export const authEndPath = '/connect/end'

export const sessionStateKey = 'session_stateKey'
export const sessionUserInfoTokenKey = 'session_user_info_token'
export const sessionAccessTokenKey = 'session_access_token'
export const sessionIdTokenKey = 'session_id_token'

export const isProduction = process.env.NODE_ENV === 'production'

export interface AuthUserInfoResponse {
    sub: string
    email: string
    email_verified: boolean
    family_name: string | undefined // not sure how it gets in this state.
    given_name: string
    updated_at: number
    'swm:subscription:entitlements': string
    'swm:subscription:occupant_id': string
    'swm:subscription:subscription_id': string
    'swm:subscription:coral_id'?: string // Will be present if metadata service enabled on that given env
    'swm:subscription:subscription_type'?: string
}

export interface SSWRedirectAuthParams {
    redirect_uri: string
    state: string
}

export interface IdTokenState {
    idToken: string
}

export interface IdTokenDecodedJWT {
    iss: string
    iat: number
    exp: number
    sub: string
    aud: string
    at_hash: string
}

// uri safe base 64 string that can be used in query params
function toURLSafeBase64(str: string) {
    let base64 = null

    if (typeof window === 'undefined') {
        base64 = Buffer.from(str).toString('base64')
    } else {
        base64 = btoa(str)
    }

    return encodeURIComponent(base64)
}

export function getOidcCallbackURI(
    publicUrl: string,
    sectionMeta?: SectionMetaInfoDTO,
) {
    const url = getBaseUrl(publicUrl, sectionMeta)
    return `${url}${authCallbackPath}`
}

export const getOidcSessionEndURI = (
    publicUrl: string,
    sectionMeta?: SectionMetaInfoDTO,
) => {
    const url = getBaseUrl(publicUrl, sectionMeta)
    return `${url}${authEndPath}`
}

export function getLoginUrl() {
    if (isServerEnvironment()) {
        throw new Error('getLoginUrl should not be called during SSR')
    }

    return `${authLoginPath}?state=${getEncodedState({
        originUrl: window.location.href,
    })}`
}

export const getAuthParams = (params: {
    sectionMeta?: SectionMetaInfoDTO
    stateGenerator?: () => string
    originUrl?: string
}): SSWRedirectAuthParams => {
    const { sectionMeta, stateGenerator, originUrl } = params
    return {
        redirect_uri: getOidcCallbackURI(location.origin, sectionMeta),
        state: stateGenerator
            ? stateGenerator()
            : getEncodedState({ originUrl }),
    }
}

interface SubscribeLinkOptions {
    config: BaseClientConfig
    pageUrl: string
    campaign?: string
    source?: string
    auth: Pick<SSWRedirectAuthParams, 'redirect_uri' | 'state'>
    packagePath?: string
    medium?: string
    additionalParams?: { [key: string]: string | undefined }
    offerCode?: string
    returningUser?: boolean
}

export const getSubscribeUrl = ({
    config,
    pageUrl,
    auth,
    campaign = 'nav-subscription-button',
    source = 'thewest.com.au',
    packagePath = 'subscription-packages',
    medium = click_origin_subscribe,
    additionalParams,
    offerCode,
    returningUser,
}: SubscribeLinkOptions) => {
    const subscriberLink =
        config.subscribeTheWestUrl || 'https://subscriber.thewest.com.au'
    const { redirect_uri, state } = auth
    const isReturningUser = returningUser ? 'true' : undefined

    const params: { [key: string]: string | undefined } = {
        utm_source: source,
        utm_medium: medium,
        utm_campaign: campaign,
        redirect_origin: pageUrl,
        redirect_uri,
        state,
        ...additionalParams,
        ...(offerCode && { offerCode }),
        ...(offerCode && { redirect: 'true' }), // This tells SSW to redirect to the form with the offer code applied
        returningUser: isReturningUser,
    }

    const path = !offerCode ? packagePath : 'offercheck/thewest'

    Object.keys(params).forEach(
        (key) =>
            (params[key] === undefined || params[key] === '') &&
            delete params[key],
    )

    return `${subscriberLink}/${path}?${queryString.stringify(params)}`
}

export interface StateTokenCookie {
    stateToken: string
    originUrl?: string
    /** To be used to bypass a state check on the return, query string will be decoded instead - should encrypt this / whole state for future security */
    bypassStateCheck?: boolean
}

/**
 * Creates, stores and returns a State token
 */
export function getEncodedState(
    params: { originUrl?: string; bypassStateCheck?: boolean },
    store = storeCookie,
): string {
    const { originUrl, bypassStateCheck } = params
    const state: StateTokenCookie = {
        stateToken: generateRandomAuthString(),
        originUrl,
        bypassStateCheck,
    }

    if (!bypassStateCheck) {
        store(getAuthStateTokenKey(state), state, {
            secure: isProduction,
            sameSite: 'lax',
        })
    }

    const stringToken = JSON.stringify(state)
    return toURLSafeBase64(stringToken) // getEncodedState used Client side
}

/**
 * Generates a random string that fits within OAuth spec
 */
export const generateRandomAuthString = () =>
    Array.from(Array(7)).map(slug).join('')

export function getAuthStateTokenKey(state: StateTokenCookie): string {
    // The user can open multiple premium articles and go to the login form in multiple tabs
    // Each article tab creates its own state token before sending the user to the login page on the auth site
    // Incorporate the state token into the cookie key so the tabs dont override each other's state token
    return `${sessionStateKey}-${state.stateToken.substring(0, 8)}`
}

export interface AuthTokenResponseError {
    success: false
    status: number
    error: {
        error: string
        errorDescription: string
    }
}

export const retrieveUserInfoToken = (
    req?: Request,
): AuthUserInfoResponse | undefined => {
    return retrieveCookie<AuthUserInfoResponse>(sessionUserInfoTokenKey, req)
}
export const retrieveIdToken = (req?: Request): IdTokenState | undefined => {
    return retrieveCookie<IdTokenState>(sessionIdTokenKey, req)
}

export const clearUserCookies = (req?: Request): void => {
    removeCookie(sessionUserInfoTokenKey, req)
    removeCookie(sessionAccessTokenKey, req)
    removeCookie(sessionIdTokenKey, req)
}

export function getAuthStateFromUserInfoToken(
    userInfo: AuthUserInfoResponse,
    onEvent: (loginEvent: LoginEvent | LoginRefreshEvent) => void,
): LoginDetails & {
    onEvent: (loginEvent: LoginEvent | LoginRefreshEvent) => void
} {
    const firstName = userInfo.given_name
    const lastName = userInfo.family_name
    const entitlements = userInfo['swm:subscription:entitlements']
    const occupantId = userInfo['swm:subscription:occupant_id']
    const gigyaId = userInfo.sub
    const subscriptionType = (userInfo['swm:subscription:subscription_type'] ||
        'none') as SubscriptionType
    const subscriptionId = userInfo['swm:subscription:subscription_id']

    return {
        entitlements: entitlements ? entitlements.split('|') : [],
        gigyaId,
        occupantId,
        loginProvider: '',
        onEvent,
        socialProviders: '',
        userName: [firstName, lastName].filter((x) => !!x).join(' '),
        subscriptionType,
        subscriptionId,
    }
}

export function decodeToken<T>(accessToken: string): T {
    return decode(accessToken)
}

export function daysToMs(days: number) {
    return days * 24 * (60 * 60 * 1000)
}

/**
 * Used by smedia to refresh the web view client side.
 */
export function setupAuthRefreshCallback(
    dispatch: Dispatch<any>,
    onEvent: (event: any) => void,
    logger: Logger,
) {
    // Calling this will return nothing in the app webview evaluation but it should dispatch authCheckIdToken
    window.smediaAuthRefresh = function (accessToken?: string) {
        const fetchUrl = accessToken
            ? `/app-login?noRedirect&accessToken=${accessToken}`
            : '/app-login?noRedirect'

        fetch(fetchUrl, {
            method: 'GET',
            credentials: 'include', // Don't forget to specify this if you need cookies
        })
            .then((res) => {
                if (res.status === 200) {
                    dispatch(
                        authCheckAccessToken({
                            onEvent,
                            logger,
                            invokation: 'manual',
                        }),
                    )
                    logger.debug('🦄 smediaAuthRefresh success')
                } else if (res.status === 401) {
                    throw new Error(
                        'accessToken not present on authentication response or expired, unable to get user Info',
                    )
                } else {
                    throw new Error('Call to login on the app failed')
                }
            })
            .catch((error: Error) => {
                logger.warn({ error }, 'smediaAuthRefresh failure')
                throw new Error(error.message)
            })
    }
}
export interface AccessTokenState {
    expiryDate: number
    accessToken: string
}

export const retrieveAccessToken = (
    req?: Request,
): AccessTokenState | undefined => {
    return retrieveCookie<AccessTokenState>(sessionAccessTokenKey, req)
}
